import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { adminListDemoRequests, adminUpdateDemoRequest } from '../api';

// v3_fix_issues #10/#14 - the website owner's queue of "Request a Demo" submissions. A secondary
// (analytics-style) admin view, so it lives behind the "More" menu.
const STATUSES = ['new', 'contacted', 'scheduled', 'closed'];
const VARIANT = { new: 'warning', contacted: 'info', scheduled: 'primary', closed: 'secondary' };

export default function DemoRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (status) => {
    setLoading(true);
    setError('');
    try {
      setRequests(await adminListDemoRequests(status || undefined));
    } catch {
      setError('Could not load demo requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const paged = usePagedArray(requests, { resetKey: statusFilter });

  const setStatus = async (id, status) => {
    try {
      await adminUpdateDemoRequest(id, { status });
      load(statusFilter);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update that request.');
    }
  };

  const toggleFollowUps = async (request) => {
    try {
      await adminUpdateDemoRequest(request._id, { followUpsStopped: !request.followUpsStopped });
      await load(statusFilter);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to change follow-ups.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="small text-muted">Show</span>
        <Form.Select size="sm" style={{ maxWidth: 180 }} aria-label="Demo request status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Form.Select>
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : paged.total === 0 ? (
        <Alert variant="light">No demo requests.</Alert>
      ) : (
        <>
        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>When</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Portal</th>
              <th>Preferred</th>
              <th>Message</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paged.rows.map((r) => (
              <tr key={r._id}>
                <td className="small">{new Date(r.createdAt).toLocaleDateString('en-IN')}</td>
                <td>{r.name}</td>
                <td className="small">{r.email}{r.phone ? <div className="text-muted">{r.phone}</div> : null}</td>
                <td className="small">{r.vertical || 'homepage'}</td>
                <td className="small">
                  {[r.preferredDate ? new Date(r.preferredDate).toLocaleDateString('en-IN') : '', r.preferredTime].filter(Boolean).join(' ') || '—'}
                </td>
                <td className="small" style={{ maxWidth: 260 }}>{r.message || '—'}</td>
                <td>
                  <Form.Select
                    size="sm" aria-label={`Status for ${r.name}`} value={r.status}
                    onChange={(e) => setStatus(r._id, e.target.value)}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                  <Badge bg={VARIANT[r.status] || 'secondary'} className="mt-1">{r.status}</Badge>
                  <div className="mt-2">
                    <Button size="sm" variant={r.followUpsStopped ? 'outline-primary' : 'outline-danger'} onClick={() => toggleFollowUps(r)}>
                      {r.followUpsStopped ? 'Resume follow-ups' : 'Stop follow-ups'}
                    </Button>
                    {r.followUpsStopped && <div className="small text-muted">Follow-up emails and WhatsApp reminders stopped.</div>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...paged} />
        </>
      )}
    </div>
  );
}
